import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Particles from "@/components/Particles";
import { CountUp, Reveal } from "@/components/motion";
import { GradientMesh, ShimmerText, Odometer } from "@/components/fx";
import Tilt from "@/components/Tilt";
import CreatorCard from "@/components/CreatorCard";
import PlatformIcon from "@/components/PlatformIcon";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import LandingSections from "@/pages/LandingSections";
import Footer from "@/components/Footer";

const HERO_LINES = ["Where every stream", "finds its destiny."];

function SneakPeeks() {
  const shots = [
    { src: "/assets/peek-overview.png", title: "Command Center", desc: "Tips, earnings, followers and live status at a glance", testid: "peek-overview" },
    { src: "/assets/peek-widgets.png", title: "Widget Library", desc: "282 alert & overlay combinations with one-click OBS links", testid: "peek-widgets" },
    { src: "/assets/peek-earnings.png", title: "Earnings Studio", desc: "Tips, brand deals and payouts tracked separately", testid: "peek-earnings" },
  ];
  return (
    <section className="py-20 sm:py-28" data-testid="sneak-peeks-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-dt-accent text-center mb-3">Inside the studio</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-dt-text text-center">Sneak a peek at the creator dashboard</h2>
          <p className="mt-3 text-sm sm:text-base text-dt-muted text-center max-w-2xl mx-auto">
            Real screens from the Destiny Tip studio — every approved creator gets this command center.
          </p>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {shots.map((s, i) => (
            <Reveal key={s.testid} delay={i * 0.12}>
              <Tilt max={7}>
                <figure className="group card-dt !rounded-2xl overflow-hidden hover:border-dt-accent/50 transition-colors duration-300" data-testid={s.testid}>
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-dt-border bg-dt-bg2">
                    <span className="w-2 h-2 rounded-full bg-[#ef4444]/60" />
                    <span className="w-2 h-2 rounded-full bg-[#f59e0b]/60" />
                    <span className="w-2 h-2 rounded-full bg-[#22c55e]/60" />
                    <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-dt-muted font-bold">{s.title}</span>
                  </div>
                  <div className="overflow-hidden">
                    <img src={s.src} alt={`${s.title} — creator dashboard preview`} loading="lazy"
                      className="w-full aspect-[16/10] object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" />
                  </div>
                  <figcaption className="px-4 py-3 text-xs text-dt-muted border-t border-dt-border">{s.desc}</figcaption>
                </figure>
              </Tilt>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function LiveNow() {
  const [items, setItems] = useState([]);
  const [demo, setDemo] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/creators/live")
      .then((r) => { if (mounted) { setItems(r.data.items || []); setDemo(!!r.data.demo); setLoaded(true); } })
      .catch(() => mounted && setLoaded(true));
    load();
    const t = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <section id="live" className="py-24 sm:py-32 bg-dt-bg2/40" data-testid="live-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#4ade80] mb-3 flex items-center gap-2">
                <span className="live-dot w-2 h-2 rounded-full bg-[#22c55e]" /> Live right now
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-dt-text">Creators streaming this moment</h2>
              {demo && (
                <p className="mt-2 text-xs text-dt-muted" data-testid="demo-data-note">
                  Showing demo creators — live platform data activates once Twitch/YouTube API keys are configured.
                </p>
              )}
            </div>
            <Link to="/creators" className="inline-flex items-center gap-1.5 text-sm text-dt-accent hover:text-dt-accent2 transition-colors" data-testid="view-all-creators-link">
              View All Creators <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        {!loaded ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[1, 2, 3, 4].map((i) => <div key={i} className="card-dt h-56 animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <p className="text-dt-muted text-sm" data-testid="no-live-creators">No creators are live right now — check back soon or browse the directory.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <CreatorCard creator={c} compact />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Landing() {
  useSeo("Destiny Tip — Creator Tipping & Stream Tools", "Tip your favourite streamers on Twitch, YouTube and Kick. 100% of every tip reaches the creator — alerts, widgets, goals, chat moderation and payouts in one ecosystem.", { path: "/" });
  const reduced = useReducedMotion();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 600], [0, reduced ? 0 : 120]);

  return (
    <div className="bg-dt-bg min-h-screen text-dt-text" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-20" data-testid="hero-section">
        <motion.div style={{ y: yBg }} className="absolute inset-0" aria-hidden="true">
          <GradientMesh />
          <Particles density={90} />
          <Particles density={34} speed={2.6} className="opacity-70" />
          <Particles density={16} speed={0.4} className="opacity-40" />
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-dt-accent/10 blur-[140px]" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p
            initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-bold text-dt-accent border border-dt-accent/30 bg-dt-accent/5 rounded-full px-5 py-2"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5" /> The Creator Destiny
          </motion.p>
          <h1 className="mt-8 font-heading font-bold tracking-tighter leading-[1.05] text-4xl sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <motion.span key={line} className="block text-metallic"
                initial={reduced ? false : { opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.15 }}>
                {i === HERO_LINES.length - 1 ? <ShimmerText>{line}</ShimmerText> : line}
              </motion.span>
            ))}
          </h1>
          <motion.p
            initial={reduced ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 text-base sm:text-xl text-dt-muted max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subcopy"
          >
            A creator-focused ecosystem bringing streaming tools, audience support, community
            interactions, and smarter workflows together.
          </motion.p>
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/apply"
              className="glow-pulse bg-dt-silver text-dt-bg font-semibold rounded-full px-8 py-3.5 text-base transition-transform duration-200 hover:scale-105 hover:bg-white"
              data-testid="hero-apply-btn">
              Apply as a Creator
            </Link>
            <a href="#features"
              className="bg-dt-card text-dt-text border border-dt-border rounded-full px-8 py-3.5 text-base font-medium hover:border-dt-silver transition-colors"
              data-testid="hero-explore-btn">
              Explore Destiny Tip
            </a>
          </motion.div>

          <div className="mt-10 flex items-center justify-center gap-6"
            aria-label="Supported platforms"
          >
            {["twitch", "youtube", "kick"].map((p, i) => (
              <span key={p} className="float-slow" style={{ animationDelay: `${i * 0.7}s` }}>
                <PlatformIcon platform={p} className="w-7 h-7 opacity-80" />
              </span>
            ))}
          </div>
        </div>
      </section>

      <SneakPeeks />

      {/* STATS */}
      <section className="border-y border-dt-border bg-dt-bg2/60 py-14" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[
            { to: 50, suffix: "+", label: "Tools in one ecosystem" },
            { to: 3, suffix: "", label: "Platforms connected" },
            { to: 100, suffix: "%", label: "Of tip reaches creators" },
            { to: 24, suffix: "/7", label: "Ecosystem always on" },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-metallic">
                <Odometer value={s.to} suffix={s.suffix} />
              </p>
              <p className="mt-2 text-xs sm:text-sm text-dt-muted tracking-wide uppercase">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <LiveNow />
      <LandingSections />
      <Footer />
    </div>
  );
}
